import * as XLSX from "xlsx";

export const MAX_LABEL_IMPORT_CODES = 2000;

const CODE_HEADER_RE = /^(code|kodi|kod|product_code|sku)$/i;

/**
 * Parse product codes from a CSV/XLSX/XLS file.
 * Prefers a column named code/kodi/kod/product_code/sku; otherwise uses the first column.
 * Dedupes while preserving file order.
 *
 * @param {File} file
 * @returns {Promise<string[]>}
 */
export async function parseProductCodesFromFile(file) {
  if (!file) {
    throw new Error("Nuk u zgjodh asnjë skedar.");
  }

  const name = String(file.name || "").toLowerCase();
  if (!/\.(csv|xlsx|xls)$/.test(name)) {
    throw new Error("Formati duhet të jetë CSV, XLSX ose XLS.");
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("Skedari nuk ka fletë të vlefshme.");
  }

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });

  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("Skedari është bosh.");
  }

  const codes = extractCodesFromRows(rows);
  if (codes.length === 0) {
    throw new Error("Nuk u gjet asnjë kod produkti në skedar.");
  }
  if (codes.length > MAX_LABEL_IMPORT_CODES) {
    throw new Error(`Maksimumi është ${MAX_LABEL_IMPORT_CODES} kode. Skedari ka ${codes.length}.`);
  }

  return codes;
}

/**
 * @param {Array<Array<unknown>>} rows
 * @returns {string[]}
 */
export function extractCodesFromRows(rows) {
  if (!rows.length) return [];

  const firstRow = (rows[0] || []).map((cell) => String(cell ?? "").trim());
  let columnIndex = 0;
  let startRow = 0;

  const headerIndex = firstRow.findIndex((cell) => CODE_HEADER_RE.test(cell));
  if (headerIndex >= 0) {
    columnIndex = headerIndex;
    startRow = 1;
  }

  const seen = new Set();
  const codes = [];

  for (let i = startRow; i < rows.length; i++) {
    const row = rows[i] || [];
    const raw = row[columnIndex];
    const code = String(raw ?? "").trim();
    if (!code) continue;

    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push(code);
  }

  return codes;
}
